import type { AnalyticsReport } from "@server/types/analyticsReportTypes";
import { endpointIdentity, endpointProfile } from "../utils/endpointMetrics";

/** Displays measured request and error counts for one UTC bucket, including all HTTP endpoints. */
export function RequestVolumeTooltip({
  active,
  timestamp,
  report,
}: {
  active?: boolean;
  timestamp: string;
  report: Pick<AnalyticsReport, "timeseries" | "endpoints">;
}) {
  const bucket = report.timeseries.find(
    (point) => point.timestamp === timestamp,
  );
  if (!active || !bucket) return null;
  const rows = report.endpoints
    .flatMap((endpoint) => {
      const bucket = endpoint.timeseries?.find(
        (point) => point.timestamp === timestamp,
      );
      return bucket && bucket.requests > 0 ? [{ endpoint, bucket }] : [];
    })
    .sort(
      (a, b) =>
        endpointProfile(a.endpoint).order - endpointProfile(b.endpoint).order ||
        endpointIdentity(a.endpoint).localeCompare(
          endpointIdentity(b.endpoint),
        ),
    );
  const count = (n: number) => n.toLocaleString();
  return (
    <div className="request-volume-breakdown" role="tooltip">
      <div className="request-volume-date">
        {new Date(timestamp).toLocaleString("en-US", {
          timeZone: "UTC",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })}{" "}
        UTC
      </div>
      <div className="request-volume-total">
        <span>Total requests</span>
        <strong>{count(bucket.requests)}</strong>
      </div>
      <div className="request-volume-status">
        <span>
          <i style={{ background: "var(--chart-1)" }} />
          Successful
        </span>
        <strong>{count(bucket.requests - bucket.errors)}</strong>
      </div>
      <div className="request-volume-status">
        <span>
          <i style={{ background: "var(--chart-5)" }} />
          HTTP errors
        </span>
        <strong>{count(bucket.errors)}</strong>
      </div>
      {rows.length ? (
        <table>
          <thead>
            <tr>
              <th>Endpoint</th>
              <th>Requests</th>
              <th>Errors</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ endpoint, bucket: point }) => (
              <tr key={endpointIdentity(endpoint)}>
                <td>
                  <span>{endpointProfile(endpoint).name}</span>
                  <small>{endpoint.method}</small>
                </td>
                <td>{count(point.requests)}</td>
                <td>{count(point.errors)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="request-volume-empty">No requests in this interval</div>
      )}
    </div>
  );
}
